import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const HelpModal = ({ open, onClose }) => {
  const { user } = useSelector((state) => state.auth);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    if (submitting) return;
    setTitle("");
    setMessage("");
    setError("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Please provide a subject for your request.");
      return;
    }
    if (!message.trim()) {
      setError("Please describe how we can help.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        "/api/help",
        { title: title.trim(), message: message.trim() },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      toast.success("Your message has been sent. We'll be in touch soon.");
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
        <HelpOutlineIcon sx={{ color: "#E67E22" }} />
        <Typography variant="h6" sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
          Get Help
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 2, fontFamily: "Inter, sans-serif" }}
          >
            Send a message to our support team and we'll get back to you.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Subject"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            disabled={submitting}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            multiline
            rows={5}
            disabled={submitting}
            inputProps={{ maxLength: 5000 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={submitting} sx={{ fontFamily: "Inter, sans-serif" }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              fontFamily: "Inter, sans-serif",
              backgroundColor: "#E67E22",
              "&:hover": { backgroundColor: "#D35400" },
            }}
          >
            {submitting ? "Sending..." : "Send Message"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default HelpModal;
